import {GenerateSurveyFormValues} from "./generate-survey.types";
import {useFormik} from "formik";
import {B2BButton} from "../../components/b2bbutton/b2bbutton";
import {InputTextarea} from "primereact/inputtextarea";
import { useEffect, useState } from "react";
import { SurveyTemplate } from "src/redux/survey/template.types";
import * as Yup from 'yup';
import { InputSwitch } from "primereact/inputswitch";
import { RadioButtonGrid, RadioButtonGridType } from "src/components/radio-button/radio-button";

type InputSectionProps = {
  readonly title: string;
  readonly subtitle: string;
  readonly placeholder: string;
  readonly field: string;
  readonly required: boolean;
};

interface StructuredData {
  required_inputs: { [key: string]: string };
  optional_inputs: { [key: string]: string };
  audience: string;
  is_rtr: boolean;
  rtr_size?: '300x250' | '970x250';
}

const generateValidationSchema = (formSections: InputSectionProps[]) => {
  const schemaFields = formSections.reduce((acc, section) => {
    acc[section.field] = section.required
      ? Yup.string().required(`${section.title} is required`)
      : Yup.string().nullable();
    return acc;
  }, {} as { [key: string]: any });
  return Yup.object().shape(schemaFields);
};

interface GenerateSurveyFormProps {
  isLoading: boolean;
  onSubmit: (template_id: string, values: GenerateSurveyFormValues) => void;
  template: SurveyTemplate | null;
}

const GenerateSurveyForm = ({ isLoading, onSubmit, template }: GenerateSurveyFormProps) => {
  const [formSections, setFormSections] = useState<InputSectionProps[] | null>(null);
  const [initialValues, setInitialValues] = useState<{ [key: string]: string }>({});
  const [isRTR, setIsRTR] = useState(false);
  const [rtrSize, setRtrSize] = useState<'300x250' | '970x250'>('300x250');

  useEffect(() => {
    if (template) {
      const sections: InputSectionProps[] = [];
      template.userInputs.requiredInputs.forEach(input => {
        sections.push({
          title: input.inputDisplayName,
          subtitle: input.inputDisplaySummary,
          placeholder: input.value,
          field: input.inputName.toLowerCase(),
          required: true,
        });
      });
      template.userInputs.optionalInputs.forEach(input => {
        sections.push({
          title: input.inputDisplayName,
          subtitle: input.inputDisplaySummary,
          placeholder: input.value,
          field: input.inputName.toLowerCase(),
          required: false,
        });
      });
      if (template.includeAudience) {
        sections.push({
          title: 'Audience',
          subtitle: 'Who is the ideal respondent for this survey? Describe the target personas or demographics.',
          placeholder: 'Adults aged 25-45, living in urban areas.',
          field: 'audience',
          required: false,
        });
      }
      setFormSections(sections);
      setInitialValues(Object.fromEntries(sections.map(section => [section.field, ''])));
    }
  }, [template]);

  const formik = useFormik({
    initialValues,
    onSubmit: values => {
      const structuredData: StructuredData = {
        required_inputs: {},
        optional_inputs: {},
        audience: values.audience || '',
        is_rtr: isRTR,
        rtr_size: isRTR ? rtrSize : undefined,
      };

      formSections?.forEach(section => {
        if (section.required) {
          structuredData.required_inputs[section.field] = values[section.field];
        } else if (section.field !== 'audience') {
          structuredData.optional_inputs[section.field] = values[section.field];
        }
      });
      onSubmit(template?.id || "", structuredData);
    },
    validateOnBlur: true,
    validationSchema: generateValidationSchema(formSections || []),
  });

  const renderInputSection = (index: number, { field, subtitle, placeholder, title, required }: InputSectionProps) => {
    const { handleChange, handleBlur, values, touched, errors } = formik;
    return (
      <div
        className="flex flex-row gap-4 mt-8"
        key={index}
      >
        <div className="flex flex-col" >
          <div className="flex justify-center items-center border border-gray-light-10 size-8 rounded-full" >
            {index + 1}
          </div>
        </div>
        <div className="flex flex-col grow gap-2 mt-[5px]">
          <div className="text-base font-bold text-black mb-[-5px]">
            {title}
            {required && (
              <span className="text-red-500 ml-1 text-xs align-top">
                *
              </span>
            )}
          </div>
          <div className="text-sm text-status-gray min-w-0 max-w-[400px] text-wrap">
            {subtitle}
          </div>
          <InputTextarea
            className="w-full border-gray-light-10 border rounded-lg p-2 h-[88px] shadow-sm lg:w-[400px] resize-none text-sm"
            id={field}
            name={field}
            placeholder={placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values[field]}
          />
          {touched[field] && errors[field] ? (
            <div className="text-red-501 text-sm mt-1 min-w-0 max-w-[400px] text-wrap">
              {errors[field]}
            </div>
          ) : null
          }
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={formik.handleSubmit}>

      {formSections?.map((section, index) => renderInputSection(index, section))}

      <hr className="mt-5 w-full ml-4 mb-5" />

      <div className="text-base font-bold text-black ml-4">
        Settings
      </div>

      <div className="flex flex-col ml-4">
        <div className="flex items-center gap-2 mb-4">
          <InputSwitch
            id="isRTR"
            onClick={() => setIsRTR(!isRTR)}
            checked={isRTR}
            className={`custom-switch ${isRTR ? 'p-inputswitch-checked' : ''}`}
          />
          <label
            htmlFor="isRTR"
            className="text-sm font-medium text-gray-700"
          >
            RTR Survey
          </label>
        </div>
        {isRTR && (
          <div className="mx-auto">
            <RadioButtonGrid
              subtext="Select the size of the RTR survey"
              buttons={[{ label: '300x250', value: '300x250' }, { label: '970x250', value: '970x250' }]}
              extSurveyId={''}
              initialValue={rtrSize}
              onClick={(value) => setRtrSize(value as '300x250' | '970x250')}
              title=""
              type={RadioButtonGridType.RtrSize}
            />
          </div>
        )}
      </div>
      <div className="mt-8 ml-12">
        <B2BButton
          label={'Generate survey'}
          isLoading={isLoading}
          disable={!formik.isValid || !formik.dirty || isLoading}
          expand
          theme="dark"
          type="submit"
        />
      </div>
    </form>
  );
};

export default GenerateSurveyForm;
